import { Send } from 'lucide-react';
import * as React from 'react';
import { formatDuration, formatPeriod, periodMonthName } from '@cfl/shared';

import {
  Alert,
  AlertDescription,
  Badge,
  Card,
  CardContent,
} from '@/components/cargofl-ui';
import { ConfirmButton } from '@/components/confirm-dialog';
import type { WeekPeriod } from '@/lib/timesheet-api';

/**
 * Where this week stands, and the one action available on it.
 *
 * Shared by both views of the week. It used to live inside the day list, which is
 * why the grid - the screen where a week actually gets filled in - had no way to
 * submit the week it had just filled. The totals somebody agrees to are the ones
 * on their screen, whichever surface drew them.
 *
 * Four states, each with a different next step, so they are not collapsed into a
 * single badge: nothing submitted yet (submit it), waiting (nothing to do but
 * wait), approved (done), sent back (fix it and submit again). A week with no
 * hours in it offers no submit button at all — the API refuses that, and a button
 * that always errors is worse than no button.
 *
 * A week that crosses a month end has two parts, and gets one line per part, each
 * with its own state and its own button. That is the whole point of the split:
 * September can be submitted on the 30th without locking October days nobody has
 * worked yet.
 */
export function WeekStatus({
  periods,
  busy,
  unsaved = false,
  onSubmit,
}: {
  periods: WeekPeriod[];
  /** The start of the part being submitted, while it is. */
  busy: string | null;
  /**
   * The grid holds typing not yet saved. Submit sends what the server has, so it
   * waits: submitting first locked the week with the typed hours left out.
   */
  unsaved?: boolean;
  onSubmit: (periodStart: string) => void;
}) {
  if (periods.length === 1) {
    const period = periods[0]!;
    return (
      <WholeWeek
        period={period}
        busy={busy === period.start}
        unsaved={unsaved}
        onSubmit={() => onSubmit(period.start)}
      />
    );
  }

  const next = periodMonthName(periods[1]!);
  return (
    <Card>
      <CardContent className="space-y-3 py-3">
        <p className="text-sm text-muted-foreground">
          This week crosses into {next}, so it is submitted in two parts. Each part locks
          only its own days.
        </p>
        {unsaved && <UnsavedHint />}
        <div className="divide-y rounded-md border">
          {periods.map((period) => (
            <PeriodLine
              key={period.start}
              period={period}
              busy={busy === period.start}
              disabled={busy !== null || unsaved}
              onSubmit={() => onSubmit(period.start)}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/** A week inside one month: one part, the whole week, exactly as it always was. */
function WholeWeek({
  period,
  busy,
  unsaved,
  onSubmit,
}: {
  period: WeekPeriod;
  busy: boolean;
  unsaved: boolean;
  onSubmit: () => void;
}) {
  const { status, decision, totalSeconds } = period;

  if (status === 'APPROVED') {
    return (
      <Alert>
        <AlertDescription className="space-y-1 text-sm">
          <span className="flex items-center gap-2">
            <Badge>approved</Badge>
            {decision?.by ? `Signed off by ${decision.by}.` : 'This week has been signed off.'} Ask
            your supervisor to reopen it if something needs correcting.
          </span>
          {decision?.message && <Note>{decision.message}</Note>}
        </AlertDescription>
      </Alert>
    );
  }

  if (status === 'SUBMITTED') {
    return (
      <Alert>
        <AlertDescription className="flex items-center gap-2 text-sm">
          <Badge variant="secondary">waiting for approval</Badge>
          Submitted and locked. Your supervisor has it.
        </AlertDescription>
      </Alert>
    );
  }

  const empty = totalSeconds === 0;

  return (
    <Card>
      <CardContent className="flex flex-wrap items-center justify-between gap-3 py-3">
        <div className="text-sm">
          {status === 'DENIED' ? (
            <span className="space-y-1">
              <span className="flex items-center gap-2">
                <Badge variant="destructive">sent back</Badge>
                {decision?.by ? `${decision.by} sent this back.` : 'This week was sent back.'} Correct
                it and submit again.
              </span>
              {/* The reason, on screen. It has always been required and always
                  emailed - and nowhere else, so anybody who lost the mail read
                  "correct what was flagged" with no way to learn what. */}
              {decision?.message && <Note>{decision.message}</Note>}
            </span>
          ) : unsaved ? (
            <UnsavedHint />
          ) : empty ? (
            <span className="text-muted-foreground">
              Record some hours before submitting this week.
            </span>
          ) : (
            <span className="text-muted-foreground">
              Submitting locks the week and sends it to your supervisor.
            </span>
          )}
        </div>
        <SubmitButton
          period={period}
          partial={false}
          busy={busy}
          disabled={busy || empty || unsaved}
          onSubmit={onSubmit}
        />
      </CardContent>
    </Card>
  );
}

/** One part of a week split at a month end. */
function PeriodLine({
  period,
  busy,
  disabled,
  onSubmit,
}: {
  period: WeekPeriod;
  busy: boolean;
  disabled: boolean;
  onSubmit: () => void;
}) {
  const month = periodMonthName(period);
  const { status, decision, totalSeconds, expectedSeconds } = period;
  const open = status === 'NOT_SUBMITTED' || status === 'DENIED';

  return (
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-3 py-2.5">
      <div className="min-w-0 space-y-1 text-sm">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="font-semibold uppercase tracking-wider text-[11px]">{month}</span>
          <span className="text-muted-foreground">{formatPeriod(period)}</span>
          <span className="ts-num">
            {formatDuration(totalSeconds)}
            <span className="text-muted-foreground"> of {formatDuration(expectedSeconds)}</span>
          </span>
        </div>
        {status === 'DENIED' && (
          <span className="block">
            <span className="flex items-center gap-2">
              <Badge variant="destructive">sent back</Badge>
              {decision?.by ? `${decision.by} sent this part back.` : 'This part was sent back.'}{' '}
              Correct it and submit again.
            </span>
            {decision?.message && <Note>{decision.message}</Note>}
          </span>
        )}
        {status === 'APPROVED' && decision?.message && <Note>{decision.message}</Note>}
      </div>
      {status === 'APPROVED' ? (
        <Badge>approved</Badge>
      ) : status === 'SUBMITTED' ? (
        <Badge variant="secondary">waiting for approval</Badge>
      ) : (
        open && (
          <SubmitButton
            period={period}
            partial
            busy={busy}
            disabled={disabled || totalSeconds === 0}
            onSubmit={onSubmit}
          />
        )
      )}
    </div>
  );
}

/**
 * Asks first: submitting locks the days and mails an approver, and getting them
 * back is somebody else's deliberate act.
 */
function SubmitButton({
  period,
  partial,
  busy,
  disabled,
  onSubmit,
}: {
  period: WeekPeriod;
  /** One part of a week split at a month end, rather than the whole week. */
  partial: boolean;
  busy: boolean;
  disabled: boolean;
  onSubmit: () => void;
}) {
  const label = partial ? `Submit ${periodMonthName(period)}` : 'Submit week';
  return (
    <ConfirmButton
      disabled={disabled}
      pending={busy}
      title={partial ? `Submit ${periodMonthName(period)}'s part of this week?` : 'Submit this week for approval?'}
      description={
        <>
          <strong>{formatPeriod(period)}</strong> — {formatDuration(period.totalSeconds)} recorded.{' '}
          {partial ? 'These days are' : 'The week is'} locked as soon as you submit, and your
          supervisor is emailed. To change anything afterwards you will have to ask them to
          send it back.
        </>
      }
      confirmLabel={label}
      onConfirm={onSubmit}
    >
      <Send className="size-4" />
      {busy ? 'Submitting…' : label}
    </ConfirmButton>
  );
}

function UnsavedHint() {
  return (
    <span className="block text-sm font-medium text-destructive">
      You have hours that are not saved yet. Press Save week below first, then submit.
    </span>
  );
}

/** A decision's own words, set apart from the sentence describing the decision. */
function Note({ children }: { children: React.ReactNode }) {
  return (
    <span className="mt-1 block whitespace-pre-wrap border-l-2 border-muted-foreground/30 pl-3 text-sm text-muted-foreground">
      {children}
    </span>
  );
}
